import React, { useEffect, useState, useMemo } from "react";
import { Pie, Column } from "@ant-design/plots";
import { coins_units } from "../../../../utils";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatPercentage } from "../../../../utils/numberFormat";
import { EmptyChartMessage } from "../EmptyChartMessage";
import "./TableGraf.scss";

export const fuelsGroupLabels = {
  Vhe1: "Gas Natural",
  Vhe2: "GLP",
  Vhe3: "Diésel",
  Vhe4: "Biodiésel",
  Vhe5: "Fuel/Gasolina",
  Vhe8: "Etanol",
  Vhe9: "Electricidad para vehículos",
};

// `type` (arriba) queda fijo en español porque es la clave interna que
// matchea contra DONUT_COLORS.fuels (colores) — el texto que se muestra
// (pills, tooltip, leyenda del PDF) se traduce por separado con este mapa.
// Mismo patrón que ENERGY_GROUP_LABEL_KEYS en EnergyKPIsDashboard/TableGraf.js.
export const FUELS_GROUP_LABEL_KEYS = {
  "Gas Natural": "natural_gas",
  "GLP": "energy_source_lpg",
  "Diésel": "diesel",
  "Biodiésel": "biodiesel",
  "Fuel/Gasolina": "gasoline",
  "Etanol": "ethanol",
  "Electricidad para vehículos": "electricity_vehicles",
};

export const getAnnualTotalsByGroup = (totalsPerYear, targetYear) => {
  const fuelsGroups = {
    Vhe1: ["natural_gas"],
    Vhe2: ["glp"],
    Vhe3: ["diesel"],
    Vhe4: ["biodiesel"],
    Vhe5: ["gasoline"],
    Vhe8: ["ethanol"],
    Vhe9: ["electricity_vehicles"],
  };
  const results = {};
  Object.entries(fuelsGroups).forEach(([groupKey, conceptKeys]) => {
    let total = 0;
    conceptKeys.forEach((concepto) => {
      total += totalsPerYear?.[concepto]?.[targetYear]?.total || 0;
    });
    results[groupKey] = { total };
  });
  return results;
};

// Barras de consumo (MWhe) — misma lógica que ya usaba el useMemo de FuelsBarChart.
export const buildFuelsBarChartData = (data) => {
  if (!data?.total_fuel_mwh) return [];
  return Object.entries(data.total_fuel_mwh)
    .filter(([_, { total }]) => total > 0)
    .map(([year, { total }]) => ({ year, value: total }))
    .sort((a, b) => a.year - b.year).slice(-5);
};

// Barras de costo — misma lógica que ya usaba el useMemo de FuelsBarChartCost.
export const buildFuelsBarChartDataCost = (data) => {
  if (!data?.total_fuel_cost) return [];
  return Object.entries(data.total_fuel_cost)
    .filter(([_, { total }]) => total > 0)
    .map(([year, { total, unit }]) => ({
      year,
      value: total,
      short: coins_units.find((c) => c.value === unit)?.short || "",
    }))
    .sort((a, b) => a.year - b.year).slice(-5);
};

const TableGraf = ({ filters, totales, t }) => {
  const { year } = filters || {};
  const [dataConsumo, setDataConsumo] = useState([]);
  const [dataCosto, setDataCosto] = useState([]);
  const windowWidth = useWindowWidth();

  // Configuración responsive memoizada
  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isNotebook = windowWidth >= 1024 && windowWidth <= 1500;
    return {
      isMobile,
      isSmallMobile,
      fontSize: {
        medium: isSmallMobile ? 10 : 12,
        small: isSmallMobile ? 9 : 11,
      },
      chartHeight: {
        donut: getDonutHeight(isNotebook, isMobile),
        bar: isSmallMobile ? 250 : isMobile ? 280 : 300,
      },
    };
  }, [windowWidth]);

  useEffect(() => {
    if (!totales || !year) return;
    setDataConsumo(getAnnualTotalsByGroup(totales, year));
    setDataCosto(totales);
  }, [filters, totales, year]);

  // --- CONFIGURACIÓN DE GRUPOS Y COLORES ---
  const fuelColorMapDonut = DONUT_COLORS.fuels;

  const barPalette = BAR_PALETTE;

  // --- COMPONENTE 1: DONUT ---
const FuelsDonut = ({ totalsPerYear, t }) => {
    const [hiddenTypes, setHiddenTypes] = useState([]);

    // 1. Preparar datos base
    const allData = useMemo(() => 
      Object.entries(totalsPerYear)
        .filter(([_, { total }]) => total > 0)
        .map(([groupKey, { total }]) => ({
          type: fuelsGroupLabels[groupKey],
          value: total,
        })), [totalsPerYear]);

    // 2. Filtrar para el gráfico
    const chartData = useMemo(
      () => allData.filter((d) => !hiddenTypes.includes(d.type)),
      [allData, hiddenTypes]
    );

    const totalVisible = chartData.reduce((sum, d) => sum + d.value, 0);
    const totalAll = allData.reduce((sum, d) => sum + d.value, 0);

    const config = {
      data: chartData,
      height: responsiveConfig.chartHeight.donut,
      angleField: "value",
      colorField: "type",
      scale: {
        color: {
          domain: Object.keys(fuelColorMapDonut),
          range: Object.values(fuelColorMapDonut),
        },
      },
      radius: 1,
      innerRadius: 0.6,
      legend: false,
      statistic: {
        title: false,
        content: {
          style: {
            fontSize: "16px",
            lineHeight: "1.4",
            fontWeight: "600",
            fontFamily: "Archivo",
          },
          // Restauramos el formato del centro
          content: `${t("total")}\n${formatWhole(totalVisible / 1000)} MWhe`,
        },
      },
      // 🛠️ RESTAURACIÓN DEL TOOLTIP ORIGINAL
      tooltip: {
        items: [
          (datum) => ({
            name: t(FUELS_GROUP_LABEL_KEYS[datum.type] || datum.type),
            value: `${formatWhole(datum.value / 1000)} MWhe (${totalAll > 0 ? formatPercentage((datum.value / totalAll) * 100) : formatPercentage(0)})`,
          }),
        ],
      },
    };

    if (allData.length === 0) return <div className="empty-msg">{t("no_data")}</div>;

    return (
      <>
        <div className="chartWrapper">
          <Pie {...config} />
        </div>

        {/* 🟢 SECCIÓN DE PILLS EN COLUMNAS DE 3 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            alignItems: "start",
            gap: "8px",
            marginTop: "15px",
            borderTop: "1px solid #000",
            paddingTop: "15px",
            paddingBottom: "4px",
            minHeight: "130px",
          }}
        >
          {allData.map((item) => {
            const isHidden = hiddenTypes.includes(item.type);
            const color = fuelColorMapDonut[item.type] || "#D9D9D9";

            return (
              <div
                key={item.type}
                onClick={() => setHiddenTypes(prev =>
                  prev.includes(item.type) ? prev.filter(y => y !== item.type) : [...prev, item.type]
                )}
                className="yearPill"
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  color: isHidden ? "#9CA3AF" : "#FFF",
                  padding: "6px 4px",
                  borderRadius: "20px",
                  fontSize: "10px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: `1px solid ${isHidden ? "#D1D5DB" : color}`,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "28px",
                  opacity: isHidden ? 0.7 : 1,
                  textDecoration: isHidden ? "line-through" : "none"
                }}
              >
                <span>{t(FUELS_GROUP_LABEL_KEYS[item.type] || item.type)}</span>
                {/* <span style={{ fontSize: "9px", opacity: 0.85 }}>
                  {totalAll > 0 ? ` ${((item.value / totalAll) * 100).toFixed(1)}%` : ""}
                </span> */}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // --- COMPONENTE 2: BARRAS CONSUMO ---
  const FuelsBarChart = ({ data }) => {
    const rawData = useMemo(() => buildFuelsBarChartData(data), [data]);

    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const fuelColorMap = useMemo(() => {
        const map = {};
        allYears.forEach((y, i) => map[y] = barPalette[i] || "#ccc");
        return map;
    }, [allYears]);

    const chartData = rawData.filter(d => activeYears.includes(d.year));

    const config = {
      data: chartData,
      height: responsiveConfig.chartHeight.bar,
      xField: "year",
      yField: "value",
      colorField: "year",
      scale: { color: { domain: Object.keys(fuelColorMap), range: Object.values(fuelColorMap) } },
      legend: false,
      // `xAxis`/`yAxis` es la API vieja (v1) de @ant-design/plots — la
      // instalada (v2) no la reconoce y quedaba ignorada en silencio, por
      // eso el eje Y nunca tuvo formato (mismo bug ya visto en GEI/Social).
      // La forma correcta es axis.x/axis.y.
      axis: {
        x: { labelFontSize: 10, labelFill: "#000" },
        y: { labelFormatter: (v) => formatWhole(v) },
      },
      tooltip: {
        items: [
          (datum) => ({
            name: " ",
            value: `${formatWhole(datum.value)} MWhe`,
          }),
        ],
      },
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div className="pillsRow">
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year])}
              style={{ backgroundColor: activeYears.includes(year) ? fuelColorMap[year] : "#ccc", cursor: 'pointer' }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  // --- COMPONENTE 3: BARRAS COSTO ---
  const FuelsBarChartCost = ({ data }) => {
    const rawData = useMemo(() => buildFuelsBarChartDataCost(data), [data]);

    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const fuelColorMap = useMemo(() => {
        const map = {};
        allYears.forEach((y, i) => map[y] = barPalette[i] || "#ccc");
        return map;
    }, [allYears]);

    const chartData = rawData.filter(d => activeYears.includes(d.year));

    if (!rawData.length) {
      return (
        <EmptyChartMessage
          message={
            t("complete_at_least_one_period") ||
            "Complete al menos un período para visualizar este indicador"
          }
        />
      );
    }

    const config = {
      data: chartData,
      height: responsiveConfig.chartHeight.bar,
      xField: "year",
      yField: "value",
      colorField: "year",
      scale: { color: { domain: Object.keys(fuelColorMap), range: Object.values(fuelColorMap) } },
      legend: false,
      // Ver comentario en FuelsBarChart más arriba: axis.x/axis.y es la API
      // correcta (v2), xAxis/yAxis se ignoraba en silencio.
      axis: {
        x: { labelFontSize: 10, labelFill: "#000" },
        y: { labelFormatter: (v) => formatWhole(v) },
      },
      tooltip: { items: [(d) => ({ name: "Costo", value: `${formatWhole(d.value)} ${d.short}` })] }
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div className="pillsRow">
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year])}
              style={{ backgroundColor: activeYears.includes(year) ? fuelColorMap[year] : "#ccc", cursor: 'pointer' }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="fuelsDashboardGrid">
      <div className="chartCard">
        <h3 className="cardTitle">{t("fuel_sources")}</h3>
        <FuelsDonut totalsPerYear={dataConsumo} t={t} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">{t("fuel_consumption")}</h3>
        <FuelsBarChart data={dataCosto} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">{t("fuel_costs")}</h3>
        <FuelsBarChartCost data={dataCosto} />
      </div>
    </div>
  );
};

export default TableGraf;